import React, {useState, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import {FmlxModal, FmlxButton, FmlxCheckBox} from 'fmlx-common-ui';
import cn from 'classnames';
import './FastModal.scss';
import FormattedHTMLMessage from './FormattedHTMLMessage';
import MessageContainer from './MessageContainer';

const ButtonStyle = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
};

const ButtonStyleOrder = [ButtonStyle.PRIMARY, ButtonStyle.SECONDARY, ButtonStyle.TERTIARY];

function FastModal({
  btnAction,
  show,
  header,
  content,
  onClose,
  isValid,
  className,
  closeDisabled,
  container,
  footerContent,
  customHeader,
  errorCodeNumber,
  style,
  size,
  confirmationCheckbox,
}) {
  const [isConfirmationChecked, setIsConfirmationChecked] = useState(false);

  useEffect(() => {
    if (!show && confirmationCheckbox) setIsConfirmationChecked(false);
  }, [confirmationCheckbox, show]);

  const toggleConfirmationCheckbox = useCallback(() => {
    setIsConfirmationChecked(!isConfirmationChecked);
  }, [isConfirmationChecked]);

  const getCustomHeader = () => {
    if (customHeader) return customHeader;

    if (closeDisabled) {
      return <span className="modal-header__align-left">{header}</span>;
    }

    return null;
  };

  const getContent = () => {
    let modalContent = content;

    if (errorCodeNumber > 0) {
      modalContent = (
        <div>
          <MessageContainer messageType={MessageContainer.MessageType.ERROR}>
            <span style={{color: '#db0000'}}>
              <FormattedHTMLMessage id="common.modal.error.label" values={{errorNumber: errorCodeNumber}} />
            </span>
          </MessageContainer>
          {content}
        </div>
      );
    }

    if (confirmationCheckbox) {
      modalContent = (
        <>
          {modalContent}
          <div style={{paddingTop: 10}}>
            <FmlxCheckBox
              label={confirmationCheckbox}
              size="sm"
              checked={isConfirmationChecked}
              onChange={toggleConfirmationCheckbox}
            />
          </div>
        </>
      );
    }

    return modalContent;
  };

  const getButton = () => {
    const btnProps = {
      primaryButton: {show: false},
      secondaryButton: {show: false, variant: FmlxButton.Variant.OUTLINE},
      tertiaryButton: {show: false, variant: FmlxButton.Variant.PLAIN},
    };

    for (let i = 0; i < btnAction.length; i += 1) {
      const btnItem = btnAction[i];
      let btnStyle = btnItem.style || ButtonStyleOrder[i];

      if (btnStyle) {
        let buttonType = btnItem.type;
        let isButtonDisabled = null;

        if (btnProps[`${btnStyle}Button`].show) btnStyle = 'tertiary';

        if (typeof btnItem.text === 'string' && !buttonType) {
          buttonType = btnItem.text.toLowerCase().includes('abort') ? FmlxButton.Type.DANGER : null;
        }

        if (btnItem.isValid !== undefined) isButtonDisabled = !btnItem.isValid;
        else if (btnItem.disabled !== undefined) isButtonDisabled = btnItem.disabled;
        else if (btnItem.disabledByCheckbox && confirmationCheckbox) isButtonDisabled = !isConfirmationChecked;
        else if (btnAction.length === 1 || btnItem.setButtonValidityByState) isButtonDisabled = !isValid;

        btnProps[`${btnStyle}Button`] = {
          ...btnProps[`${btnStyle}Button`],
          show: true,
          disabled: isButtonDisabled,
          label: btnItem?.label || btnItem.text,
          type: buttonType,
          onClick: btnItem?.onClick || btnItem.handlerClick,
          variant: btnItem.variant || btnProps[`${btnStyle}Button`].variant,
        };
      }
    }

    return btnProps;
  };

  const getSize = () => {
    if (typeof size === 'string') return size;
    if (errorCodeNumber > 0) return 'lg';

    if (typeof content === 'string') return 'md';
    return 'md';
  };

  return (
    <div className={cn(className, {contained: !!container})}>
      <FmlxModal
        open={show}
        onCloseClick={onClose}
        style={style}
        disableBlur
        hideCloseButton={closeDisabled}
        title={header}
        customFooter={footerContent}
        customHeader={getCustomHeader()}
        size={getSize()}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getButton()}
      >
        {getContent()}
      </FmlxModal>
    </div>
  );
}

FastModal.propTypes = {
  show: PropTypes.bool,
  header: PropTypes.string,
  content: PropTypes.any,
  btnAction: PropTypes.array,
  onClose: PropTypes.any,
  isValid: PropTypes.bool,
  className: PropTypes.string,
  closeDisabled: PropTypes.bool,
  container: PropTypes.any,
  footerContent: PropTypes.any,
  customHeader: PropTypes.any,
  errorCodeNumber: PropTypes.number,
  style: PropTypes.any,
  size: PropTypes.string,
  confirmationCheckbox: PropTypes.string,
};

FastModal.defaultProps = {
  show: false,
  header: '',
  content: null,
  btnAction: [],
  onClose: () => {},
  isValid: true,
  className: null,
  closeDisabled: true,
  container: null,
  footerContent: null,
  customHeader: null,
  errorCodeNumber: 0,
  style: {},
  size: null,
  confirmationCheckbox: undefined,
};

export default FastModal;
